import {
	catchError,
	combineLatest,
	combineLatestWith,
	distinctUntilChanged,
	filter,
	first,
	map,
	merge,
	Observable,
	of,
	pairwise,
	startWith,
	tap,
} from 'rxjs';

import {
	BitService,
	Container,
	DataService,
	HeartModalSource,
	HeartService,
	IHearts,
	IShopItem,
	ModalService,
	OwnershipRequirementType,
	Service,
	ShopItemType,
	StorageService,
	StreakService,
	SubscriptionPlanService,
} from '../../../../symphony';
import { SlActions } from '../ActionsService/sl-actions';
import { SlDataService } from '../sl-data.service';
import { SlApiService } from '../sl-api.service';
import {
	getFlashMessages,
	heartRefillLocationData,
	SlLEContext,
	SlModalIds,
} from '../../global-constants';
import {
	ActionType,
	CurrentPageType,
	HeartFullness,
	HeartOpenFrom,
	ModalKeys,
	RequestType,
} from '../../global-interface';
import { isJustCompleted, isLastLesson } from '../../utils/utilsFunctions';
import { SlStorageKeys } from '../../../../shared/public/globalInterfaces/globalInterfaces';
import { IFlashMessageTypes } from '../../../../shared/public/SlFlashMessage/SlFlashMessage';
import { MaterialType, LessonSessionQuitType, CompletionStatus } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { ShopItemTypes, IBuyShopItemResponse } from '../../../../api/public/gamification-api';
import { EngineType, IKudosResponse, IKudosHeartConfigsResponse } from '../../../../api/public/kudos-api';
import { PlanConfigKey } from '../../../../api/public/subscription-api';

@Service()
export class SlHeartsService {
	private streakService = Container.take('global', StreakService);

	private storageService = Container.take('global', StorageService);

	private heartService = Container.take('global', HeartService);

	private bitService = Container.take('global', BitService);

	private data = Container.take(SlLEContext, SlDataService);

	private globalData = Container.take('global', DataService);

	private modalService = Container.take('global', ModalService);

	private api = Container.take(SlLEContext, SlApiService);

	private actions$ = Container.take(SlLEContext, SlActions).actions$;

	private subscriptionPlanService = Container.take('global', SubscriptionPlanService);

	constructor() {
		const {
			course$, lesson$, routeData$, loading$,
		} = this.data;
		const { shopItemsList$, learningExperience$ } = this.globalData;
		const { hearts$ } = this.heartService;

		merge(
			course$.pipe(
				tap((course) => {
					this.heartService.setCourse(
						course?.materialInfo?.header?.alias,
					);
					this.streakService.setCourse(
						course?.materialInfo?.header?.alias,
						course?.materialInfo?.header?.id,
					);
				}),
			),
			lesson$.pipe(
				tap((lesson) => {
					this.heartService.setLessonId(
						lesson?.materialInfo?.header?.materialRelationId,
					);
					const session: string = this.storageService.load(
						SlStorageKeys.Sl_LE_SESSION,
						'session',
					);

					this.heartService.setLessonDetails({
						session,
					});
				}),
			),
			shopItemsList$.pipe(
				first((si) => !!si),
				combineLatestWith(hearts$.pipe(first((h) => !!h))),
				tap(([shopItems]) => this.setupHearts(shopItems)),
			),
			combineLatest([
				lesson$,
				hearts$.pipe(
					distinctUntilChanged(
						(prevHrt, currHrt) => prevHrt?.heartsCount === currHrt?.heartsCount,
					),
				),
				learningExperience$,
				loading$.pipe(startWith(null), pairwise()),
			]).pipe(
				filter(
					([lsn, hrt, le]) => !!lsn
						&& !!hrt
						&& !!le
						&& le.ownership.ownershipRequirementsTypeId !== OwnershipRequirementType.Paid
						&& this.heartService.isRanOutOfHearts(hrt),
				),
				tap(([, , , [prevLoading, currentLoading]]) => {
					if (
						currentLoading?.solveMaterial !== true
						&& prevLoading?.solveMaterial === true
					) {
						this.onLessonRanOutOfHearts();
					}
				}),
			),
			routeData$.pipe(
				pairwise(),
				tap(([prevValue, currValue]) => {
					if (
						prevValue?.currentPage === CurrentPageType.LessonPage
						&& currValue?.currentPage !== CurrentPageType.LessonPage
					) {
						this.heartService.heartModal$.next({
							...this.heartService.heartModal$.value,
							source: null,
						});
						this.heartService.leaveLessonModal$.next({
							...this.heartService.leaveLessonModal$.value,
							isActive: false,
						});
					}
				}),
			),
		).subscribe();
	}

	public setHeartsData = (hearts: IHearts): Observable<null> => {
		this.heartService.hearts$.next(hearts);
		return of(null);
	};

	private shouldOpenFreeHeartRefillModal = (): boolean => {
		const { lesson$, heartConfigs$, course$ } = this.data;

		if (
			this.subscriptionPlanService.getConfig(PlanConfigKey.hearts) === 'unlimited'
			|| lesson$.value.materialInfo.header.typeId === MaterialType.ModuleQuiz
			|| !heartConfigs$.value
			|| heartConfigs$.value.firstLessonFreeHeartReceived
		) {
			return false;
		}

		const { parentRelationId } = lesson$.value.materialInfo.header;
		const parentOrderNumber = course$.value?.children.find(
			(module) => module.materialInfo.header.materialRelationId === parentRelationId,
		).materialInfo.orderNumber;

		return (
			heartRefillLocationData.moduleOrderNumber === parentOrderNumber
			&& heartRefillLocationData.lessonOrderNumber
			=== lesson$.value.materialInfo.orderNumber
		);
	};

	private shouldOpenSpentLastHeartInLastQuizModal = () => {
		const { lesson$, failedSolutionsCount, question$ } = this.data;
		return (
			failedSolutionsCount?.[
			question$.value?.header?.materialRelationId
			] === 1
			&& isLastLesson()
			&& isJustCompleted()
			&& lesson$.value.materialInfo.header.typeId !== MaterialType.Booster
		);
	};

	private onLessonRanOutOfHearts = (): void => {
		if (this.shouldOpenFreeHeartRefillModal()) {
			this.modalService.open(SlModalIds.freeHeartModal);
			this.data.modals$.next({
				...this.data.modals$.value,
				[ModalKeys.FreeHeartRefill]: true,
			});
		} else {
			const heartModalSource = this.shouldOpenSpentLastHeartInLastQuizModal()
				? HeartModalSource.SpentLastHeartInLastQuiz
				: HeartModalSource.SpentLastHeart;
			this.actions$.next({
				type: ActionType.OpenHeartModal,
				requestType: RequestType.Parallel,
				data: {
					heartModalSource,
				},
			});
		}
	};

	private setupHearts = (shopItems: IShopItem[]): Observable<null> => {
		this.heartService.setup(
			shopItems.find((si) => si.id === ShopItemType.HeartRefill)?.price,
			{
				refillByBits: this.refillByBits,
				navigateToCourse: this.navigateToCourse,
				openLesson: this.openLesson,
				onClose: this.onHeartModalClose,
				toNextPage: this.toNextPage,
			},
		);

		return of(null);
	};

	private toNextPage = (): void => {
		this.actions$.next({
			type: ActionType.CompleteLesson,
			requestType: RequestType.Parallel,
		});
	};

	public openHeartModal = (
		source: HeartModalSource,
		refillByBitsCallback?: () => void,
	): Observable<null> => {
		if (refillByBitsCallback) {
			this.heartService.setRefillByBitsCallback(refillByBitsCallback);
		}
		this.heartService.openModal$.next(source);
		return of(null);
	};

	private refillByBits = (): Observable<null> => this.api
		.unlockItemWithBits({
			itemType: ShopItemTypes.heartRefill,
			itemId: 0,
		})
		.pipe(
			map(({ success, data }: IBuyShopItemResponse) => {
				if (data) {
					this.bitService.bits$.next(data);
				}
				if (success) {
					this.heartService.refillHearts();
				}

				return null;
			}),
			catchError(() => of(null)),
		);

	private navigateToCourse = (): void => {
		this.actions$.next({
			type: ActionType.NavigateToCourse,
			requestType: RequestType.Parallel,
		});

		this.actions$.next({
			type: ActionType.DeleteSessionDataFromStorage,
			requestType: RequestType.Parallel,
		});

		this.actions$.next({
			type: ActionType.DeleteSessionFromBackend,
			requestType: RequestType.Parallel,
			data: {
				lessonSessionQuitType: LessonSessionQuitType.HeartsRunOut,
			},
		});
	};

	private openLesson = (id: number): void => {
		this.actions$.next({
			type: ActionType.OpenLesson,
			requestType: RequestType.Parallel,
			data: {
				id,
			},
		});
	};

	private onHeartModalClose = (): void => {
		const { lesson$ } = this.data;

		if (
			lesson$.value?.materialInfo.status.completion === CompletionStatus.ByPassed
		) {
			this.actions$.next({
				type: ActionType.TryAgain,
				requestType: RequestType.Parallel,
			});
		}
	};

	private refillFreeHeartsErrorHandler = (): void => {
		const { loading$, modals$ } = this.data;
		loading$.next({
			unlock: false,
		});
		this.data.flashMessage$.next({
			type: IFlashMessageTypes.ERROR,
			message: getFlashMessages().failed,
		});
		this.modalService.close(SlModalIds.freeHeartModal);
		modals$.next({
			[ModalKeys.FreeHeartRefill]: false,
		});
	};

	private refillFreeHeartsResponseHandler = (heartData: IHearts): void => {
		const { heartConfigs$, loading$, modals$ } = this.data;
		loading$.next({
			unlock: false,
		});
		this.heartService.hearts$.next(heartData);
		heartConfigs$.next({
			...heartConfigs$.value,
			firstLessonFreeHeartReceived: true,
		});
		this.actions$.next({
			type: ActionType.TryAgain,
			requestType: RequestType.Parallel,
		});
		this.modalService.close(SlModalIds.freeHeartModal);
		modals$.next({
			[ModalKeys.FreeHeartRefill]: false,
		});
	};

	public refillFreeHearts = () => {
		const { loading$, lesson$ } = this.data;
		loading$.next({
			unlock: true,
		});
		return this.api
			.refillFreeHearts({
				entityId: lesson$.value.materialInfo.header.materialRelationId,
				entityType: 1,
				engineType: EngineType.newEngine,
			})
			.pipe(
				tap((heartData: IKudosResponse) => {
					this.refillFreeHeartsResponseHandler(heartData.data);
				}),
				catchError(() => {
					this.refillFreeHeartsErrorHandler();
					return of(null);
				}),
			);
	};

	public getHeartConfigs = (): Observable<IKudosHeartConfigsResponse> => this.api.getHeartConfigs().pipe(
		tap(({ data }) => {
			this.data.heartConfigs$.next(data);
		}),
		catchError(() => of(null)),
	);

	public trackFirstRefillImpression = (): Observable<null> => {
		this.heartService.trackFirstRefillImpression();
		return of(null);
	};

	public trackFirstRefillClick = (): Observable<null> => {
		this.heartService.trackFirstRefillClick();
		return of(null);
	};
}
